import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, ArrowRight } from 'lucide-react';
import SEOPageLayout from '../components/SEOPageLayout';

export default function AgentIncomeCalculator() {
  const [dealSize, setDealSize] = useState(2000000);
  const [commRate, setCommRate] = useState(3);
  const [dealsPerYear, setDealsPerYear] = useState(6);

  const gross = dealSize * (commRate / 100);
  const krieger = gross * 0.9;
  const trad80 = gross * 0.8;
  const trad70 = gross * 0.7;
  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  return (
    <SEOPageLayout
      badge="Income Calculator"
      title="Calculate Your Real Estate Earnings at Krieger"
      subtitle="See exactly how much more you'd earn with Krieger's up-to-90% split compared to your current brokerage. Works for residential sales, rentals, and commercial transactions."
      metaTitle="Real Estate Agent Income Calculator | Compare Commission Splits | Krieger"
      metaDescription="Calculate how much more you'd earn at Krieger Real Estate. Up to 90% commission on residential and commercial deals vs. 70-80% at traditional brokerages. Free calculator."
      metaKeywords="real estate agent income calculator, commission split calculator, how much do real estate agents make, compare brokerage commission, real estate earnings, switch broker save money"
      canonicalPath="/agent-income-calculator"
    >
      <div className="space-y-10">
        <div className="bg-white border border-slate-200 rounded-xl p-7">
          <h2 className="text-lg font-['Cabinet_Grotesk'] font-bold mb-6 text-slate-900">Your Deal Profile</h2>
          <div className="space-y-6">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-slate-400 font-semibold block mb-2">Average Deal Size: {fmt(dealSize)}</label>
              <input type="range" min="500000" max="50000000" step="250000" value={dealSize} onChange={(e) => setDealSize(Number(e.target.value))} className="w-full" data-testid="calc-deal" />
              <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>$500K</span><span>$50M</span></div>
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-slate-400 font-semibold block mb-2">Commission Rate: {commRate}%</label>
              <input type="range" min="1" max="6" step="0.5" value={commRate} onChange={(e) => setCommRate(Number(e.target.value))} className="w-full" data-testid="calc-rate" />
              <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>1%</span><span>6%</span></div>
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-slate-400 font-semibold block mb-2">Deals Per Year: {dealsPerYear}</label>
              <input type="range" min="1" max="50" step="1" value={dealsPerYear} onChange={(e) => setDealsPerYear(Number(e.target.value))} className="w-full" data-testid="calc-deals-year" />
              <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>1</span><span>50</span></div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border-2 border-[#002FA7]/20 p-6 rounded-xl" data-testid="result-krieger">
            <div className="flex items-center gap-2 mb-1"><DollarSign size={14} className="text-[#002FA7]" /><span className="text-xs font-semibold text-[#002FA7] uppercase tracking-wider">Krieger 90/10</span></div>
            <div className="text-3xl font-['Cabinet_Grotesk'] font-bold text-[#002FA7] mt-2">{fmt(krieger)}</div>
            <div className="text-xs text-slate-400 mt-1">per deal</div>
            <div className="border-t border-slate-100 mt-4 pt-4">
              <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-900">{fmt(krieger * dealsPerYear)}</div>
              <div className="text-xs text-slate-400">annual income ({dealsPerYear} deals)</div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 p-6 rounded-xl">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Traditional 80/20</div>
            <div className="text-3xl font-['Cabinet_Grotesk'] font-bold text-slate-400 mt-2">{fmt(trad80)}</div>
            <div className="text-xs text-slate-400 mt-1">per deal</div>
            <div className="border-t border-slate-100 mt-4 pt-4">
              <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-400">{fmt(trad80 * dealsPerYear)}</div>
              <div className="text-xs text-slate-400">annual income</div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 p-6 rounded-xl">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Traditional 70/30</div>
            <div className="text-3xl font-['Cabinet_Grotesk'] font-bold text-slate-400 mt-2">{fmt(trad70)}</div>
            <div className="text-xs text-slate-400 mt-1">per deal</div>
            <div className="border-t border-slate-100 mt-4 pt-4">
              <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-400">{fmt(trad70 * dealsPerYear)}</div>
              <div className="text-xs text-slate-400">annual income</div>
            </div>
          </div>
        </div>

        <div className="bg-[#EFF3FF] border border-[#002FA7]/10 rounded-xl p-7 text-center">
          <div className="text-xs tracking-[0.15em] uppercase text-slate-500 font-semibold mb-2">Your Extra Annual Earnings vs 70/30</div>
          <div className="text-4xl font-['Cabinet_Grotesk'] font-bold text-emerald-600">+{fmt((krieger - trad70) * dealsPerYear)}</div>
          <div className="text-sm text-slate-500 mt-2">That's {fmt(krieger - trad70)} more per deal, {dealsPerYear} times a year.</div>
        </div>

        <div className="text-center">
          <Link to="/commission-plans" className="text-sm text-[#002FA7] border border-[#002FA7]/20 px-5 py-2 rounded-full hover:bg-[#EFF3FF] transition-colors mr-3">View Commission Plans</Link>
          <Link to="/apply" className="text-sm bg-[#002FA7] text-white px-6 py-2.5 rounded-full hover:bg-[#0040D6] transition-colors inline-flex items-center gap-2">Apply Now <ArrowRight size={14} /></Link>
        </div>
      </div>
    </SEOPageLayout>
  );
}
